import React from 'react';
import { Grid } from '@material-ui/core';
import styled from 'styled-components/macro';
import { Input, Button, Select } from '@deeptrue-frontend/ui';
import { Formik, Field as FormikField, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import ErrorMessages from 'components/Fields/ErrorMessages';
import { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import Fields from 'components/Fields';

export interface FormValues {
  first_name: string;
  last_name: string;
  language: string;
  organization: string;
  phone: string | null;
  username: string;
  non_field_errors?: any;
}

export type SubmitHandler = (
  values: FormValues,
  helpers: FormikHelpers<FormValues>
) => void;

const StyledForm = styled.form`
  margin-top: 1rem;
  width: 100%;
`;

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    organization: Yup.string(),
    username: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
    first_name: Yup.string(),
    last_name: Yup.string(),
    phone: Yup.string().nullable(),
    language: Yup.string(),
  });

interface Props {
  initialValues: FormValues;
  onSubmit: SubmitHandler;
}
const Profile = ({ onSubmit, initialValues }: Props) => {
  const t = useFormatMessage();
  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={getValidationSchema(t)}
    >
      {({
        handleSubmit,
        isSubmitting,
        errors,
        touched,
        setFieldValue,
        values,
      }) => {
        const nonFieldErrors = errors.non_field_errors;
        return (
          <Grid item xs={12} md={7}>
            <StyledForm onSubmit={handleSubmit}>
              <FormikField
                as={Input}
                name="organization"
                label={t('forms.profile.organization', 'Your organization')}
                disabled
                data-testid="organization"
              />

              <FormikField
                as={Input}
                label={t('forms.profile.username', 'Username')}
                name="username"
                hasError={touched.username && errors.username}
                helperText={touched.username && errors.username}
                data-testid="username"
              />

              <FormikField
                as={Input}
                label={t('forms.profile.firstname', 'First name')}
                name="first_name"
                hasError={touched.first_name && errors.first_name}
                helperText={touched.first_name && errors.first_name}
                data-testid="firstName"
              />

              <FormikField
                as={Input}
                label={t('forms.profile.lastname', 'Last name')}
                name="last_name"
                hasError={touched.last_name && errors.last_name}
                helperText={touched.last_name && errors.last_name}
                data-testid="lastName"
              />

              <FormikField
                as={Select}
                label={t('user.language', 'Language')}
                name="language"
                hasError={touched.language && errors.language}
                helperText={touched.language && errors.language}
                onChange={setFieldValue}
                options={[
                  {
                    value: 'en',
                    label: 'English',
                  },
                  {
                    value: 'fr',
                    label: 'Français',
                  },
                  {
                    value: 'de',
                    label: 'Deutsch',
                  },
                  {
                    value: 'it',
                    label: 'Italiano',
                  },
                ]}
                data-testid="lang"
              />

              <Fields.Phone
                name="phone"
                label={t('forms.profile.phonenumber', 'Phone number')}
                hasError={touched.phone && errors.phone}
                helperText={touched.phone && errors.phone}
                onChange={setFieldValue}
                value={values.phone || ''}
                data-testid="phone"
              />

              {nonFieldErrors && <ErrorMessages errors={nonFieldErrors} />}
              <Button
                mt="3rem"
                width="100%"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                type="submit"
                data-testid="submit"
              >
                {t('forms.profile.save', 'Save')}
              </Button>
            </StyledForm>
          </Grid>
        );
      }}
    </Formik>
  );
};

export default Profile;
